import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchTimesheetStatus } from "../api";
import {
  PageHeader, Card, Field, Select, Stat, Loading, ErrorState, EmptyState,
  useHorizontalScrollShadow,
} from "../components/ui";
import { hrs, fmtDate } from "../lib/format";
import { readUi, writeUi } from "../lib/persist";

/**
 * Who has not filled in their timesheet.
 *
 * Deliberately not a utilisation page: leave and sickness are logged like any
 * other time, so a week off still counts as *entered*. The question here is
 * only whether a timesheet exists at all, which is what the back office chases.
 */

/** A normal full week. Used to separate "barely logged" from "looks complete". */
const FULL_WEEK = 37.5;
const LOW = 0.6;                       // below 60% of a week reads as partial

const weekLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

/**
 * Timesheets.
 *
 * Chasing who has not filled theirs in is an admin job, so this lives under
 * Admin. `embedded` drops the page chrome — Admin supplies the heading and the
 * section switcher — and moves the header's controls onto a toolbar of their
 * own, since they do the work and cannot go with the title.
 */
export default function Timesheets({ username, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isFd = String(username).toLowerCase() === "fd";
  const [manager, setManager] = useState(() => readUi("ts.manager", "All"));
  const [weeks, setWeeks] = useState(() => readUi("ts.weeks", 5));
  const [onlyGaps, setOnlyGaps] = useState(false);

  const scrollRef = useHorizontalScrollShadow();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    fetchTimesheetStatus(weeks)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [weeks]);

  useEffect(load, [load]);
  useEffect(() => { writeUi("ts.manager", manager); }, [manager]);
  useEffect(() => { writeUi("ts.weeks", weeks); }, [weeks]);

  const managers = useMemo(
    () => [...new Set((data?.rows || []).map((r) => r.partner).filter(Boolean))].sort(),
    [data]
  );

  /** One row per person, hours keyed by week start. */
  const people = useMemo(() => {
    if (!data) return [];
    const by = new Map();
    for (const r of data.rows) {
      if (manager !== "All" && r.partner !== manager) continue;
      if (!by.has(r.employee_code)) {
        by.set(r.employee_code, {
          code: r.employee_code,
          name: r.employee_name,
          partner: r.partner,
          lastEntry: r.last_entry,
          hours: {},
        });
      }
      by.get(r.employee_code).hours[r.wk_start] = Number(r.hours) || 0;
    }
    return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, manager]);

  /**
   * A week counts once the report was run at or after it started. People log
   * on Fridays, so a week that has begun is fair game even if its last days
   * are still blank - that lag is the thing being chased, not a data gap.
   *
   * With no run stamp, nothing is excluded: showing a week that turns out to
   * be unreported is a smaller error than hiding one that is genuinely late.
   */
  const reportable = useCallback((wkStart) => {
    if (!data?.dataThrough) return true;
    return new Date(`${wkStart}T00:00:00`) <= new Date(data.dataThrough);
  }, [data]);

  const missingCount = useCallback(
    (p) => (data?.weeks || []).filter((w) => reportable(w) && !p.hours[w]).length,
    [data, reportable]
  );

  const shown = useMemo(
    () => (onlyGaps ? people.filter((p) => missingCount(p) > 0) : people),
    [people, onlyGaps, missingCount]
  );

  const summary = useMemo(() => {
    const chase = people.filter((p) => missingCount(p) > 0);
    const blank = people.filter(
      (p) => (data?.weeks || []).every((w) => !reportable(w) || !p.hours[w])
    );
    return { people: people.length, chase: chase.length, blank: blank.length };
  }, [people, missingCount, data, reportable]);

  const frame = (body) => (embedded
    ? <div className="stack">{body}</div>
    : <main className="page">{body}</main>);

  if (loading) return frame(<Loading label="Loading timesheets…" />);
  if (error) return frame(<ErrorState message={error} onRetry={load} />);

  const wks = data?.weeks || [];

  const inner = (
    <>
      {(() => {
        const sub = `${wks.length}-week rolling check`
          + (data?.dataThrough ? ` · report run ${fmtDate(data.dataThrough)}` : "")
          + (data?.coverage ? ` · ${data.coverage}` : "");
        const controls = (
          <>
        {isFd && (
          <Field label="Manager">
            <Select
              value={manager}
              onChange={setManager}
              options={[
                { value: "All", label: "All managers" },
                ...managers.map((m) => ({ value: m, label: m.toUpperCase() })),
              ]}
              style={{ minWidth: 130 }}
            />
          </Field>
        )}
        <Field label="Weeks">
          <Select
            value={String(weeks)}
            onChange={(v) => setWeeks(Number(v))}
            options={[4, 5, 6, 8, 12].map((n) => ({ value: String(n), label: `${n} weeks` }))}
          />
        </Field>
        <button
          className={`btn${onlyGaps ? " is-on" : ""}`}
          onClick={() => setOnlyGaps((v) => !v)}
        >
          {onlyGaps ? "Showing gaps only" : "Show gaps only"}
        </button>
          </>
        );
        return embedded ? (
          <div className="ts-subhead">
            <span className="muted">{sub}</span>
            <div className="ts-subhead-controls">{controls}</div>
          </div>
        ) : (
          <PageHeader title="Timesheets" subtitle={sub}>{controls}</PageHeader>
        );
      })()}

      <div className="stack">
        <div className="stat-grid">
          <Stat label="People" accent="var(--s-1)" value={String(summary.people)}
                meta="active in the last 8 weeks" />
          <Stat label="To chase" accent={summary.chase ? "var(--warn)" : "var(--pos)"}
                value={String(summary.chase)} meta="missing at least one week" />
          <Stat label="Nothing at all" accent={summary.blank ? "var(--neg)" : "var(--pos)"}
                value={String(summary.blank)} meta="no hours in the whole window" />
        </div>

        {!shown.length ? (
          <Card>
            <EmptyState
              title={onlyGaps ? "Everyone is up to date" : "No people to show"}
              hint={onlyGaps ? "No missing weeks in this window." : "Try a different manager."}
            />
          </Card>
        ) : (
          <Card title="Hours entered per week" note="blank weeks are what needs chasing" flush>
            <div className="tbl-wrap tbl-wrap--flush" ref={scrollRef}>
              <table className="tbl tbl-zebra">
                <thead>
                  <tr>
                    {isFd && manager === "All" && <th style={{ width: 70 }}>Manager</th>}
                    <th className="sticky-col" style={{ minWidth: 190 }}>Employee</th>
                    {wks.map((w) => (
                      <th key={w} className="num" title={`Week commencing ${fmtDate(w)}`}>
                        {weekLabel(w)}
                        {!reportable(w) && <span className="ts-pending"> ·</span>}
                      </th>
                    ))}
                    <th className="num" style={{ width: 80 }}>Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => {
                    const miss = missingCount(p);
                    return (
                      <tr key={p.code}>
                        {isFd && manager === "All" && (
                          <td><span className="badge">{String(p.partner || "").toUpperCase()}</span></td>
                        )}
                        <td className="sticky-col">{p.name}</td>
                        {wks.map((w) => {
                          const h = p.hours[w] || 0;
                          if (!reportable(w)) {
                            return (
                              <td key={w} className="num ts-nodata"
                                  title="The report was run before this week began">
                                <span className="dash">·</span>
                              </td>
                            );
                          }
                          const cls = !h ? "ts-none" : h < FULL_WEEK * LOW ? "ts-low" : "ts-ok";
                          return (
                            <td key={w} className={`num ${cls}`}
                                title={`Week commencing ${fmtDate(w)}: ${hrs(h)} h`}>
                              {h ? hrs(h) : "—"}
                            </td>
                          );
                        })}
                        <td className={`num ${miss ? "ts-none" : ""}`} style={{ fontWeight: 650 }}>
                          {miss || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="toolbar-note dept-hint">
              Leave and sickness are logged like any other time, so a week off still
              counts as entered — a blank cell means no timesheet, not a quiet week.
              Columns marked <strong>·</strong> begin after the report was last run,
              so there is nothing to chase there yet.
            </p>
          </Card>
        )}
      </div>
    </>
  );

  return embedded ? inner : <main className="page">{inner}</main>;
}
